import React, { useState, useEffect } from 'react';
import { Modal, Box, Button, Form, Header, SpaceBetween, Link, Alert } from '@cloudscape-design/components';

import { InfoLink } from '../../commons/common-components';
import { submissionsApi } from '../../../api/Submission';
import DistributionsPanel from './distribution-panel';
import { useBreadcrumbs } from '../../../components/breadcrumbs/index';
import { useNavigate } from 'react-router-dom';
 

export function FormHeader({ loadHelpPanelContent }) {
  return (
    <Header
      variant="h1"
      info={<InfoLink id="form-main-info-link" onFollow={() => loadHelpPanelContent(0)} />}
      description="When you create an Amazon CloudFront distribution, you tell CloudFront where to find your content by specifying your origin servers."
    >
      Submit the Indicator Evidences 
    </Header>
  );
}


const AIConfirmationModal = ({ 
  visible, 
  onDismiss, 
  onConfirm,
  isProcessing
}) => {
  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="AI Review Confirmation"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button 
              variant="link" 
              onClick={onDismiss}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={onConfirm}
              loading={isProcessing}
            >
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <p>
        Are you sure you want to get AI assessments?
      </p>
    </Modal>
  );
};


function FormActions({ onCancelClick, onSubmitClick, onRecommendationHintsClick, isLoading, isGenAILoading, isFramework2, validateEvidenceFiles, validateJudgment, validateWordLimit, setAIError }) {
  const [showAIConfirmation, setShowAIConfirmation] = useState(false);

  const handleSaveAndGenAI = () => {
    if (process.env.REACT_APP_FRAMEWORK == 2 && validateWordLimit && !validateWordLimit()) {
      setAIError('Text exceeds the 250-word limit. Please reduce the content.');
      // Scroll to description field
      const textareaElement = document.querySelector('textarea');
      if (textareaElement) {
        textareaElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        textareaElement.focus();
      }
      return;
    }
    if (isFramework2 && validateJudgment && !validateJudgment()) {
      setAIError('Please set judgment before requesting AI review.');
      return;
    }
    if (isFramework2 && validateEvidenceFiles && !validateEvidenceFiles()) {
      setAIError('All evidence sections must have at least one file uploaded before requesting AI review.');
      return;
    }
    setShowAIConfirmation(true);
  };

  const handleAIConfirm = async () => {
    await onRecommendationHintsClick();
    setShowAIConfirmation(false);
  };

  return (
    <>
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant='normal' onClick={onCancelClick}>
          Cancel
        </Button>
        <Button 
        variant='normal'
          data-testid="create-genai" 
          onClick={handleSaveAndGenAI}
        >
          Save & GenAI Review
        </Button>
        <Button 
          data-testid="create" 
          variant="primary"
          loading={isLoading}
          onClick={() => {
            if (isFramework2 && validateJudgment && !validateJudgment()) {
              setAIError('Please set judgment before saving.');
              return;
            }
            onSubmitClick();
          }}
        >
          Save
        </Button>
      </SpaceBetween>

      <AIConfirmationModal
        visible={showAIConfirmation}
        onDismiss={() => setShowAIConfirmation(false)}
        onConfirm={handleAIConfirm}
        isProcessing={isGenAILoading}
      />
    </>
  );
}
function BaseForm({ 
  content, 
  onCancelClick, 
  errorText = null, 
  onSubmitClick, 
  header, 
  onRecommendationHintsClick, 
  onJudgmentClick,
  isLoading, 
  isGenAILoading,
  aiError = null,
  submissionId,
  isFramework2 = false,
  validateEvidenceFiles,
  validateJudgment,
  validateWordLimit,
  setAIError
}) {
  const navigate = useNavigate();
  return (
    <form
      onSubmit={event => {
        event.preventDefault();
      }}
    >
      <SpaceBetween size="m">
        
        <Form
          header={header}
          actions={
            <FormActions 
              onCancelClick={() => navigate(`/application-details/${submissionId}`)} 
              onSubmitClick={onSubmitClick} 
              onRecommendationHintsClick={onRecommendationHintsClick}
              onJudgmentClick={onJudgmentClick}
              isLoading={isLoading}
              isGenAILoading={isGenAILoading}
              isFramework2={isFramework2}
              validateEvidenceFiles={validateEvidenceFiles}
              validateJudgment={validateJudgment}
              validateWordLimit={validateWordLimit}
              setAIError={setAIError}
            />
          }
          errorText={aiError}
          errorIconAriaLabel="Error"
        >
          {content}
        </Form>
      </SpaceBetween>
         </form>
      );
}

const defaultData = {
  sslCertificate: 'default',
  cloudFrontRootObject: '',
  alternativeDomainNames: '',
  s3BucketSelectedOption: null,
  certificateExpiryDate: '',
  certificateExpiryTime: '',
  httpVersion: 'http2',
  ipv6isOn: false,
  functions: [],
  evidences: [],
  originId: '',
  customHeaders: [{}],
  codeEditor: '',
};

export function FormFull({ loadHelpPanelContent, header, indicatorSubmissionID }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isGenAILoading, setIsGenAILoading] = useState(false);
  const [data, _setData] = useState(defaultData);
  const [aiError, setAIError] = useState(null);
  const [isFramework2, setIsFramework2] = useState(false);
  const [validateEvidenceFiles, setValidateEvidenceFiles] = useState(null);
  const [validateJudgment, setValidateJudgment] = useState(null);
  const [validateWordLimit, setValidateWordLimit] = useState(null);
  
  const { setActiveBreadcrumb } = useBreadcrumbs();
  
  useEffect(() => {
    const fetchIndicatorSubmission = async () => { 
      const indicatorSubmissionData = await submissionsApi.getIndicatorSubmission(indicatorSubmissionID);
      
      // Check if it's Framework 2
      const framework2 = process.env.REACT_APP_FRAMEWORK == 2;
      setIsFramework2(framework2);
      
      // Handle new data structure with evidences array
      if (indicatorSubmissionData.evidences && Array.isArray(indicatorSubmissionData.evidences)) {
          // ✅ Store only file metadata, not File objects
          indicatorSubmissionData.evidences = indicatorSubmissionData.evidences.map(evidence => ({
            evidence_id: evidence.evidence_id,
            evidence_title: evidence.evidence_title,
            evidence_description: evidence.evidence_description,
            files: evidence.files.map(file => ({
              fileID: file.id,
              file: {
                name: file.original_filename,
                size: file.size || 0,
                type: file.mime_type || 'application/octet-stream',
                lastModified: new Date(file.created).getTime()
              }
            }))
          }));
        
      } else if (indicatorSubmissionData.files) {
        // Old structure - convert files to evidences format
        indicatorSubmissionData.evidences = indicatorSubmissionData.files.map((file) => ({
          fileID: file.id,
          file: {
            name: file.original_filename,
            size: file.size || 0,
            type: file.mime_type || 'application/pdf',
            lastModified: new Date(file.created).getTime()
          }
        }));
      }
      
      _setData(data => ({...data, ...indicatorSubmissionData}));
      setActiveBreadcrumb(
        { text: `${indicatorSubmissionData.indicator.index} ${indicatorSubmissionData.indicator.name}`, href: '#', index: 4 },
        4
      );
  
    }
    fetchIndicatorSubmission();
  }, [indicatorSubmissionID]);

  const submitIndicatorForm = async () => {
    setIsLoading(true);
    setAIError(null);
    try {
      await submissionsApi.updateIndicatorText(indicatorSubmissionID, data.text);
      await submissionsApi.updateJudgment(indicatorSubmissionID, data.judgment);
    } catch (error) {
      console.error('Failed to submit form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onRecommendationHintsClick = async () => {
    setIsGenAILoading(true);
    setAIError(null);
    try {
      if (!data.text){
        setAIError('Please enter some text before requesting an AI review.');
        return;
      }
      await submissionsApi.updateIndicatorText(indicatorSubmissionID, data.text);
      await submissionsApi.invokeAiCheck(indicatorSubmissionID);

    } catch (error) {
      if (error.response?.data?.detail) {
        const { error_code, message } = error.response.data.detail;
        switch (error_code) {
          case 1:
            setAIError('Please enter some text before requesting an AI review.');
            break;
          case 2:
            setAIError('Please attach at least one file before requesting an AI review.');
            break;
          case 3:
            setAIError('Another AI invocation is processing for this indicator');
            break;
          default:
            setAIError('An error occurred while processing your AI review request. Please try again.');
        }
      } else {
        setAIError('An unexpected error occurred. Please try again later.');
      }
    } finally {
      setIsGenAILoading(false);
    }
  };

  const onJudgmentClick = async () => {
    try {
      // Handle Framework 2 judgment functionality
      console.log('Judgment clicked for Framework 2:', data);
      // Add your judgment logic here
    } catch (error) {
      console.error('Judgment error:', error);
      setAIError('Failed to process judgment. Please try again.');
    }
  };

  const setData = (updateObj = {}) => _setData(prevData => ({ ...prevData, ...updateObj }));


  return (
    <BaseForm
      header={header}
      submissionId={data.submission_id}
      content={
        <SpaceBetween size="l">
          <DistributionsPanel data={data} setData={setData} setErrors={setAIError} isFramework2={isFramework2} indicatorSubmissionID={indicatorSubmissionID} setValidateEvidenceFiles={setValidateEvidenceFiles} setJudgmentValidation={setValidateJudgment} setValidateWordLimit={setValidateWordLimit} />
        </SpaceBetween>
      }
      onSubmitClick={submitIndicatorForm}
      onRecommendationHintsClick={onRecommendationHintsClick}
      onJudgmentClick={isFramework2 ? onJudgmentClick : null}
      isLoading={isLoading}
      isGenAILoading={isGenAILoading}
      aiError={aiError}
      isFramework2={isFramework2}
      validateEvidenceFiles={validateEvidenceFiles}
      validateJudgment={validateJudgment}
      validateWordLimit={validateWordLimit}
      setAIError={setAIError}
    />
  );
}